import { z } from 'zod';
import { MAX_PROJECTILES_PER_CANNON } from '../catalog';
import { ITEM_LEVEL_CAPS } from '../inventory/definitions';
import { pvpAppearanceSchema, pvpNicknameSchema } from '../pvp/appearance';
import { pvpHealthMilliSchema } from '../pvp/health';
import {
  openWorldBorderCancelSchema,
  openWorldBorderRefusalSchema,
  openWorldBorderStateSchema,
  openWorldChannelResultSchema,
  openWorldExtractionStartSchema,
  openWorldPortEntrySchema,
} from './border';
import {
  openWorldBossCapacityRefusalSchema,
  openWorldBossCoreSchema,
  openWorldBossEmberSchema,
  openWorldBossEntrySchema,
  openWorldBossEventSchema,
  openWorldBossGoneSchema,
  openWorldBossHomingSchema,
  openWorldBossStateSchema,
} from './bosses';
import {
  openWorldCardActivateSchema,
  openWorldCardBoardSchema,
  openWorldCardBurstSchema,
  openWorldCardFlagsSchema,
  openWorldCardHudSchema,
  openWorldCardReleaseSchema,
  openWorldCardVisualSchema,
  openWorldSummonEntrySchema,
} from './cards';
import {
  openWorldCometEntrySchema,
  openWorldCometEventSchema,
  openWorldCometGoneSchema,
  openWorldCometStateSchema,
} from './comets';
import { openWorldCrossingIntentSchema, openWorldCrossingResultSchema } from './crossing';
import { openWorldDirectionSchema } from './direction';
import { openWorldExpeditionDigestSchema, openWorldExpeditionSchema } from './expedition';
import { openWorldExtractionReceiptSchema } from './extraction';
import {
  openWorldFishAbandonSchema,
  openWorldFishingPrivateSchema,
  openWorldFishingPublicSchema,
  openWorldFishingRefusalSchema,
  openWorldFishingResultSchema,
  openWorldFishStartSchema,
  openWorldWaterAreaSchema,
} from './fishing';
import { openWorldCoordinateSchema } from './grid';
import {
  openWorldBoxIdSchema,
  openWorldBoxMarkSchema,
  openWorldBoxViewSchema,
  openWorldCargoContentsSchema,
  openWorldCargoInspectSchema,
  openWorldCargoViewSchema,
  openWorldLootCloseSchema,
  openWorldLootEndedSchema,
  openWorldLootGoldSchema,
  openWorldLootOpenSchema,
  openWorldLootResultSchema,
  openWorldLootTakeAllSchema,
  openWorldLootTakeSchema,
} from './looting';
import {
  openWorldMerchantOpenSchema,
  openWorldMerchantPresenceSchema,
  openWorldMerchantSellSchema,
  openWorldMerchantViewSchema,
  openWorldSaleReceiptSchema,
  openWorldSaleRefusalSchema,
} from './merchant';
import {
  openWorldMobEntrySchema,
  openWorldMobEventSchema,
  openWorldMobGoneSchema,
  openWorldMobStateSchema,
} from './mobs';
import {
  openWorldPlanetEntrySchema,
  openWorldPlanetEventSchema,
  openWorldPlanetGoneSchema,
  openWorldPlanetStateSchema,
} from './planets';

/**
 * **What crosses an open-world quadrant's socket, in both directions.**
 *
 * ## Its own contract, and its own version
 *
 * Version 1, and it starts at 1 rather than continuing anybody else's numbering.
 * The duel's protocol, the hunt's checkpoint, the cards and the jewels all have
 * versions of their own, and nothing here may move any of them: a quadrant is a
 * different room with a different shape - many hulls instead of two or six, an
 * interest area instead of a whole arena, a disconnection that leaves a wreck
 * standing instead of forfeiting a match.
 *
 * Widening the duel's schema to carry all that would have made every duel frame
 * pay for fields it never sets, and would have tied two features that must be
 * able to change independently.
 *
 * ## The browser sends INTENT, never state
 *
 * There is no position, no velocity, no health, no target, no hit, no kill and
 * no damage in the client-to-server schema, and there must never be one. What a
 * player may say is: which way I am holding, where I am pointing, and whether I
 * am holding fire. Everything else is the room's to decide.
 *
 * ## What two things are borrowed, and why that is not a second catalogue
 *
 * The APPEARANCE and the HEALTH codec come from `../pvp/`. They are named for
 * the duel because the duel needed them first, and they are the project's only
 * definitions of what a ship looks like and how a life is written down. A
 * second appearance schema here would be a second skin catalogue, and a second
 * health codec would be a second answer to "how many digits is a life" - and
 * the first of the two to drift would be the one nobody edited.
 *
 * The coupling is deliberate and it is stated: if the appearance contract
 * changes shape, this protocol changes with it, because there is one fleet.
 *
 * ## Short field names, on purpose
 *
 * This schema is sent twenty times a second to every connection in a quadrant,
 * and a quadrant holds more hulls than a duel does. `x` instead of `positionX`
 * is not premature optimisation at that rate; the readable names live in the
 * TypeScript types, where they cost nothing.
 */

/* -------------------------------------------------------------------------- */
/*  Version and limits                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The open world room protocol's version. **2.**
 *
 * Bumped when the meaning of a field changes. Independent of
 * `OPEN_WORLD_GRID_VERSION` and `OPEN_WORLD_CONTENT_VERSION`: how the world is
 * shaped, what it is filled with, and how a room talks about it are three
 * separate questions, and re-rolling the world's content must not invalidate a
 * client that speaks this correctly.
 *
 * ## What version 2 added, and why the number had to move
 *
 * The fourteen socket jewels. A quadrant can now hold a bomb, a missile and a
 * spinning set of blades, and a hull can order its bleeding marks to burst - so
 * the wire gained an order, a kind on a projectile, a blade phase on a hull and
 * one channel for what a jewel did. A client speaking version 1 would draw a
 * bomb as an ordinary shot and would never see a saw, which is a client showing
 * a fight that is not the one being played.
 *
 * ## What version 3 added
 *
 * Crossing a border. A client may now say "I want to leave through that edge" -
 * a direction, an order id and its ordinary sequence, and nothing else - and
 * the room answers with where the handoff's state machine got to. A client
 * speaking version 2 cannot ask, and cannot be told, so the number moved.
 *
 * ## What version 4 added
 *
 * The party. A pilot now arrives knowing the frozen composition they belong to,
 * a crossing intent can say "with my party", a proposal tells the group who is
 * eligible and who has agreed, an order can be withdrawn, and a member is told
 * where their allies are - which quadrant, connected, alive, and roughly which
 * way when they are somewhere else.
 *
 * A client speaking version 3 has no shape for an ally and would draw a
 * teammate as a stranger, which is the one thing a party protocol must never
 * let happen.
 *
 * **Only this version moved.** The duel's protocol, the hunt's checkpoint, the
 * cards' and the jewels' versions are untouched, and nothing in this file
 * reaches any of them.
 *
 * ## What version 8 added
 *
 * The border's OPTIONS. The state frame now carries which edges actually lead
 * somewhere from where the hull is standing, with each destination's coordinate
 * and whether it is a safe port - so a client can offer `SUL — B/3` and
 * `NORTE — PORTO A/3` instead of four arrows, three of which are walls.
 *
 * A client speaking version 7 has no field for them and would have to derive
 * the grid's edges, the eight ports and the crossing band by itself. It did not
 * - it offered all four directions unconditionally - and the room refused three
 * of them, which is precisely the shape of bug a version exists to prevent.
 *
 * ## What version 9 added
 *
 * The comets. A state frame now carries the ones inside the viewer's interest
 * area - entering, changing, leaving - and the events they produced: an impact,
 * a part breaking, a core being revealed, a jewel being taken.
 *
 * The four fields are OPTIONAL rather than required, and that is the whole
 * reason this is a widening rather than a break: a room that has no comet in
 * anybody's view sends none of them, which is most frames of a safe port and
 * every frame of a quadrant nobody is shooting at. A client speaking version 8
 * would still parse such a frame - but it would silently ignore a rock it is
 * flying into, and `.strict()` would reject the frames that do carry one.
 *
 * **Only this version moved**, again: the duel, the hunt, the cards and the
 * jewels are untouched, and the handoff and coordinator versions did not move
 * because nothing about a crossing changed.
 *
 * ## What version 10 added
 *
 * The mining planets: the same four shapes the comets have - entering,
 * changing, leaving, and their events - under their own names, because a planet
 * is not a comet with more parts. A comet has one implicit core; a planet has
 * one to three, each with an index, and the state carries only the ones that
 * are EXPOSED. How many exist is not a field: a planet holding one item and a
 * planet holding three are indistinguishable until a core appears.
 *
 * Optional again, and for the same reason: a quadrant nobody is mining sends
 * none of them. A client speaking version 9 would parse a frame without a
 * planet and reject, through `.strict()`, every frame that carries one.
 *
 * ## What version 11 added
 *
 * The mobs: the first living things a quadrant holds. Entering, changing,
 * leaving, and their events - a blow landing, an attack, a death, a birth.
 *
 * A mob entry carries the MAP INDEX its statistics came from rather than an
 * asset id, which is what lets the art of the maps that do not exist yet appear
 * on its own the day it does, without touching this file. It does not carry
 * the mob's target: knowing who something is chasing on the other side of the
 * quadrant would be knowing that somebody is there.
 *
 * Optional, like the two before it, and a client speaking version 10 would
 * reject through `.strict()` every frame that carries one.
 *
 * ## What version 12 added
 *
 * The bosses: one to three per dangerous quadrant, the first thing in the open
 * world that announces an attack before it can land.
 *
 * A boss entry carries the MAP INDEX for the same reason a mob's does, plus the
 * ROLE - melee or caster - because the client draws a discreet indicator for it
 * rather than a different sprite. A boss state carries the CAST in progress:
 * which spell, which phase, how far the charge has gone and which way it points,
 * so the warning can be read and stepped out of.
 *
 * What a boss frame does NOT carry is its package: not the contents, not the
 * count, and not a field where either could go. The one exception is `bc`, the
 * capacity refusal, which is addressed to the pilot who just landed the fatal
 * blow and did not fit - and to nobody else.
 *
 * Optional, like the others, and a client speaking version 11 would reject
 * through `.strict()` every frame that carries one.
 *
 * ## What version 13 added
 *
 * The water, and fishing.
 *
 * A quadrant's water areas become public - they are places, and a place you can
 * see is a place you can decide to fly to. What is NOT public is what lives in
 * them: there is no population field, no stock, no species and no weight on any
 * frame that more than one pilot receives.
 *
 * `fp` says who is fishing, in which water, and in which phase. `fm` is the
 * private half, addressed to the fisher's connection alone, and its `f` is
 * `null` until the bite - which is why cancelling before the bite cannot be used
 * to shop for a prize: there is nothing to see, and no field where it could
 * have been.
 *
 * Two client orders join the frame union - `fs` to start and `fx` to cancel -
 * and two server frames answer them, `fr` and `ff`, both to one connection.
 *
 * ## What version 14 added
 *
 * The fishing merchant, in the eight safe ports.
 *
 * `mk` is public - a trading station is a landmark, and seeing one is being
 * able to decide to fly to it. `mn` is per-connection and authoritative: it is
 * the server saying "your hull is close enough", and it is what decides whether
 * the button exists. The client never concludes proximity from what it draws,
 * and never sends a distance.
 *
 * Two client orders join the union - `mo` to ask for the counter and `ms` to
 * sell - and three server frames answer them: `mv` with the two sellable lists,
 * `mr` with the receipt, `mf` with a refusal. All three go to one connection:
 * what a pilot carries in their hold and what they own in their collection are
 * theirs.
 *
 * **No price, rarity, weight, multiplier or quantity ever travels upward.** A
 * sell order carries an intent and, at most, line identifiers the server reads
 * back; a "sell all" carries no list at all.
 *
 * ## What version 15 added
 *
 * Death boxes became visible, and a hold became inspectable.
 *
 * `ke`/`kx` publish the boxes in the interest area as a MARK: an opaque id, a
 * position, and the milliseconds left. The contents, the item ids and the gold
 * are absent, and there is no field where they could go - the full contents
 * already had a frame (`lb`) that goes only to the connection that opened the
 * box, and that is where they stay. `kn` is the per-connection answer to "which
 * box may I open", which is what decides whether the button exists.
 *
 * `cq`/`ci` are the pilot's own hold: an order that decides nothing, and a
 * private list that answers it. `cg` already carried the counts; what was
 * missing was the list, and it is read-only - there is no order to discard,
 * move, equip, consume, reorder or transfer, which is the shape of those things
 * not existing rather than the shape of them being refused.
 *
 * The five loot orders and the three loot answers are unchanged: this gate gave
 * them a consumer, and messages that merely gained one do not move a version.
 *
 * ## What version 16 added
 *
 * **The sixteen cards.** They ran in the duel and did nothing here: the loadout
 * was frozen with the rest of the hull and no message could reach it.
 *
 * Three intents go up — `ka` activates, `kr` releases a charge, `kb` asks to
 * board a Leviathan — and each carries a slot number and its own identity, and
 * NOTHING else. There is no field for the owner (it comes from the socket), for
 * a level, a cooldown, a target, a charge duration, damage or a result. A
 * repeat is READ rather than obeyed twice, which is what the identity is for.
 *
 * One private frame comes down (`kc`): the three slots of the connection that
 * owns them, with what is running and what is left. A cooldown is decision
 * information and never travels to an opponent.
 *
 * The state frame gained what a scene cannot draw without: the public card
 * flags on a hull and on a creature (`fz`, `bt`, `mw`, `pz`, `lt`, `ft`, `cc`,
 * `sk`), what a hull's cards are DRAWING (`cv`) with the two charge fractions
 * (`cg`, `cw`) and the temporary cannons (`sc`), and the summons in the
 * interest area (`sm` / `sr`). The letters are the duel's, deliberately: the
 * same scene family draws them, and two spellings for one state would be two
 * chances of one of them going undrawn.
 *
 * A projectile gained `cb` — which card body it is — and `br`, the radius of
 * the blast it will make, because a Fireball GROWS with its charge and what is
 * seen has to be what catches.
 *
 * ## What version 17 added
 *
 * One list: `kd`, the card explosions that just happened. It exists because
 * version 16 published them on the JEWEL channel with the Atomic Bomb's id, and
 * a scene chooses art by ID and never by size — so a Fireball, a Catolé and an
 * Atomic Core were one thing to whoever drew them. The duel gives the two card
 * bursts identities of their own; this is the same fact on this world's wire.
 *
 * It is a field because it is NOT derivable: a body leaving the view has not
 * necessarily exploded — it may have expired, left the interest area, or crossed
 * a border with its owner — and the CENTRE of a blast that lands on a hull is
 * the hull's position, not the body's last published one.
 *
 * The jewel channel is untouched, and a Drone's kamikaze still travels on it
 * with `atomic_blast`: that is exactly what the duel does, and the art that id
 * names is the approved one.
 *
 * ## What version 19 REMOVED, and why a removal is a version
 *
 * **Crossing a border between quadrants is always individual.** The owner
 * decided it, and the decision deletes a mechanic rather than adding one: there
 * is no collective teleport between quadrants, for a party or for anybody else.
 * Each hull reaches the border itself, picks its own direction, channels its own
 * ten seconds into a port and crosses on its own. Allies may follow each other;
 * nobody carries anybody. See ADR 0081.
 *
 * Three shapes are gone:
 *
 * - the crossing intent's optional `g` — "with my party";
 * - `xc`, the withdrawal, which existed to take a confirmation back out of a
 *   proposal;
 * - `xg`, the proposal itself.
 *
 * The version rises because a client built against 18 still knows how to send
 * two of them, and a room speaking 19 must **refuse** what it no longer means -
 * never reinterpret a group intent as a solo one. `.strict()` on the intent and
 * the discriminated union do that by construction: `g` is now an unknown key
 * and `xc` an unknown discriminant, and both are rejected before anything reads
 * them. The handshake refuses the mismatched version first, which is where a
 * stale client is supposed to stop.
 *
 * What did NOT change: the crossing intent itself (`x`), its direction, its
 * identity, its retry semantics, `xr`, the ten-second channel into a port, the
 * border protection, the origin/destination race and the departure fence. Nine
 * members of one party who want to move are nine independent crossings.
 *
 * ## What version 20 changed: the fish can WIN
 *
 * A hull has **three minutes from the bite** to defeat a fish; the deadline
 * passing with the fish alive means it escapes, with no catch and no Gold. The
 * player may also abandon at any moment. See ADR 0083 and
 * `OPEN_WORLD_FISH_FIGHT_LIMIT_MS`.
 *
 * Three shapes move, and each of the three is why the version rises:
 *
 * - **`fm` gains `o` and `ms`.** `o` is the attempt's identity — the `fs`
 *   order that began it — and `ms` is how many milliseconds are left before the
 *   fish escapes, `null` before the bite. The remaining time is the SERVER's: a
 *   client may interpolate between frames so the counter does not jump, but the
 *   number that arrives replaces the local projection.
 * - **`fx` gains `id`.** It used to carry nothing, because cancelling decided
 *   nothing - the fish went back to the water whole. Now it escapes, so a
 *   duplicate or delayed `fx` could end a DIFFERENT attempt: the one the player
 *   started in between. The id names the attempt, and an id that is not the
 *   current one does nothing at all.
 * - **`fr.e` gains `escaped` and renames `cancelled` to `abandoned`.** The
 *   rename is not cosmetic: "cancelled" described an action with no consequence,
 *   and abandoning now costs the fish.
 *
 * **An absent `ms` may never be read as an unlimited fight.** A client built
 * against 19 is refused by the handshake, which is where a stale client is
 * supposed to stop - it never gets to interpret a missing field.
 *
 * What did NOT change: `fs`, `fp`, `ff`, the reveal, the water areas, the
 * population secret, the prices and the capacity rules.
 *
 * ## What version 21 added: a boss's magic is visible to whoever it is aimed at
 *
 * A boss's **Fire Sphere** was simulated, persisted and hurt people, and it
 * travelled on no public channel at all: a pilot took the damage and saw the
 * explosion of something they were never shown approaching. That is an
 * authoritative mechanical fact hidden from the person it lands on.
 *
 * **The sphere needs no new shape.** It travels on the flying-body channel that
 * already exists — `pe`/`pu`/`px` — with the SAME fields a card's Fireball
 * already uses: `cb: 'f'` and `br`, the blast radius. The geometry matches on
 * both sides (the body is `cardSphereHitRadius` of the radius either way), so
 * the client draws it through the path it already draws the player's sphere
 * with, with the same particles, core, halo and trail.
 *
 * One field is genuinely new, and it is why the version rises:
 *
 * - **`bv[].r`** — the blast radius of a boss `impact`, in milli-units, and
 *   OPTIONAL. Absent means the impact had no area, which is every Solar Lance
 *   parcel: those are a hit on one hull and their effect is unchanged. Present
 *   is a Fire Sphere bursting, and the drawing now opens exactly as far as the
 *   damage reached — before this, the client opened a constant of its own, so
 *   what was seen was not what hurt.
 *
 * **Nothing was removed and nothing shrank.** A client built against 20 is
 * refused by the handshake, which is where a stale client stops.
 *
 * ## What version 22 added: a straight body says so, and then stops repeating
 *
 * A shot, a Super Cannon round, a summon's round, a bomb, a reflection and a
 * boss's Fire Sphere all fly in a **straight line at a constant speed**: their
 * velocity is `readonly` and the one place that changes it refuses anything
 * that is not steering. And yet the room re-sent `i/x/y` for each of them,
 * twenty times a second, to every viewer — a position the client could have
 * derived exactly.
 *
 * One optional field:
 *
 * - **`pe[].l`** — "this body is linear". Present means the position is
 *   `x + vx x (tick - entryTick)`, one step per authoritative tick, until the
 *   `px`. Absent keeps the old meaning, "wait for the `pu`", so **no existing
 *   variant becomes linear by omission**.
 *
 * The client's arithmetic is the room's arithmetic: a tick advances a body by
 * exactly `(vx, vy)` in integer milli-units — four substeps of `vx/4`
 * truncated back to `vx` — so what the client computes is **bit for bit the
 * number the room would have sent**. The frame's own `k` is the clock: a frozen
 * connection freezes the bodies, exactly as it freezes everything else, and
 * nothing is ever extrapolated off a local clock.
 *
 * Everything that steers — missiles, the Catolé, the guided darts — keeps
 * receiving `pu` in every frame. Nothing was removed, and a client built
 * against 21 is refused by the handshake.
 *
 * ## 23 — uma salva atravessa a rede uma vez (gate V28I.AI)
 *
 * Uma salva Prismatic de nível sete nasce com vinte e oito corpos que
 * **partilham** o dono, o rumo, o tipo e a marca de linearidade. A forma `pe`
 * repetia esses campos vinte e oito vezes.
 *
 * Um campo opcional novo:
 *
 * - **`pb`** — nascimentos em LOTE. Os campos comuns uma vez, e um tuplo
 *   `[i, x, y]` por corpo. Expandido no cliente pela função canónica
 *   {@link openWorldExpandProjectileBatch}, dá **exatamente** as mesmas entradas
 *   `pe` que teriam viajado: mesmas identidades, mesmas posições, mesmas
 *   velocidades, mesmas marcas, mesma ordem.
 *
 * **É transporte, e nada mais.** O servidor continua com vinte e oito entidades
 * e vinte e oito colisões; a cena continua a desenhar vinte e oito sprites,
 * vinte e oito rastos e vinte e oito impactos. `pe` **não** foi removido: um
 * observador que chega tarde, uma reconexão, uma ressincronização, um
 * subconjunto parcial, um corpo unitário e uma reflexão continuam a viajar
 * individualmente, porque nenhum deles é um lote.
 *
 * Um lote é montado **depois** da área de interesse e do Manto, com os corpos
 * que aquela ligação já ia receber — nunca antes, e nunca com um corpo que ela
 * não podia ver. Um cliente construído contra 22 é recusado pelo aperto de mão.
 *
 * ## 24 — e uma atualização também (gate V28I.AP)
 *
 * O nascimento já atravessava a rede em lote; a **atualização** não. `pu`
 * repetia `"i"`, `"x"`, `"y"`, `"vx"` e `"vy"` uma vez por corpo e por
 * pulsação — vinte e quatro bytes de chaves por corpo que nunca mudam.
 *
 * Quem chega lá é pouco e é sempre o mesmo tipo de coisa: desde o V28I.Z.3 um
 * corpo LINEAR não recebe atualização nenhuma, portanto `pu` só leva **mísseis
 * e corpos de carta teleguiados** — e todos eles mandam sempre os cinco valores.
 * Uma forma com dois campos opcionais que nunca faltam é uma forma a pagar por
 * uma variante que a sala deixou de produzir.
 *
 * Um campo opcional novo, e um que deixou de ser obrigatório:
 *
 * - **`pm`** — atualizações em LOTE. Um tuplo `[i, x, y, vx, vy]` por corpo,
 *   validado posição a posição, sem identidades repetidas e com um teto de
 *   cardinalidade. {@link openWorldExpandProjectileMove} devolve **exatamente**
 *   o objeto `pu` que teria viajado.
 * - **`pu`** — passa a opcional. Continua a ser a forma de uma atualização que
 *   não tenha os cinco valores; hoje a sala não produz nenhuma, e ausente quer
 *   dizer "nenhuma".
 *
 * **É transporte, e nada mais.** Continuam a existir os mesmos corpos, com as
 * mesmas identidades, as mesmas posições autoritativas, as mesmas trajetórias,
 * a mesma colisão individual, a mesma entrada e a mesma saída — e a cena
 * continua a desenhar cada um com o seu objeto, o seu rasto e o seu impacto.
 * Nenhum número foi quantizado, arredondado ou perdido.
 *
 * `px` **não** mudou: já era um vetor de identidades, cinco bytes por corpo, e
 * não há chave nenhuma lá para tirar. Lotear saídas não pouparia nada e mexeria
 * na ordem de um ciclo de vida que está certo.
 *
 * Um cliente construído contra 23 é recusado pelo aperto de mão.
 *
 * ## 25 — uma morte diz o que custou (gate V28I.AV)
 *
 * O recibo `xt` viaja nesta união, e a caixa que ele descreve numa morte
 * ganhou dois campos: **`items`**, quantos ficaram lá dentro, e **`gold`**, o
 * Ouro de campo que ficou com eles.
 *
 * Sem eles, tudo o que a forma pública dizia de uma nave destruída era
 * `gold: "0"` — o Ouro que passou a ser SEGURO, que numa morte é zero por
 * definição — e a coordenada do quadrante. Um piloto lia "Ouro transferido: 0",
 * que é a frase de uma extração vazia e não a de uma expedição perdida.
 *
 * Os dois são lidos da liquidação, dentro da transação que fecha a morte, e não
 * do estado da caixa quando alguém abre o recibo: uma caixa já saqueada diria
 * zero, que é verdade sobre a caixa e mentira sobre a morte.
 *
 * A extração versionada sobe com eles (`OPEN_WORLD_EXTRACTION_VERSION` 1 -> 2)
 * e o relatório persistido também (`OPEN_WORLD_EXPEDITION_REPORT_VERSION`
 * 1 -> 2), porque as três formas descrevem a mesma coisa e mentir sobre uma
 * delas seria deixar duas a discordar.
 *
 * Um cliente construído contra 24 é recusado pelo aperto de mão.
 *
 * ## 29 — os três bosses finais têm kit (gate V28I.BG)
 *
 * Os bosses 18, 19 e 20 deixaram de partilhar as duas magias PROVISÓRIAS com
 * todos os outros conjuradores e passaram a ter o kit deles: uma perseguição
 * inevitável, um golpe de pressão em intervalo sorteado pelo servidor, e um
 * derradeiro que mata quem não cumprir a mecânica anunciada.
 *
 * O que isso muda na trama:
 *
 * - **`c` desapareceu** do estado de um boss. Ele descrevia a conjuração
 *   provisória — fase, carga e direção — e a habilidade que o enchia foi
 *   removida com os onze kits definitivos. Um boss publica `s`, a habilidade em
 *   curso, e, se for final, `u`.
 * - **`u` é novo**: a ultimate em curso, com a fase, o progresso dela em
 *   milésimos, o progresso da interrupção em milésimos, as duas bordas do anel
 *   do Decreto, e **`iv`, que é privado**: cada ligação recebe a resposta sobre
 *   SI, e nunca a lista de envolvidos. Uma lista de vítimas na trama diria a
 *   toda a gente quem vai morrer.
 * - **`bh`/`bhx` e `bk`/`bkx` são novos**: os teleguiados no ar e os núcleos
 *   rúnicos da Sentença. Os dois viajam com o estado inteiro em cada pulsação,
 *   porque os dois mudam em cada tique e são poucos.
 *
 * O navegador continua a não enviar nada disto: nem progresso, nem lista de
 * envolvidos, nem dano, nem limiar, nem resultado, nem núcleo destruído, nem
 * posição de zona. Ele desenha o que a autoridade diz.
 *
 * Um cliente construído contra 28 é recusado pelo aperto de mão.
 *
 * ## 30 — os quatro obuses do Bombardeio de Profundidade (gate V28I.CF.1A)
 *
 * O bombardeio do boss 18 anunciava quatro obuses e não publicava nenhum: o
 * aviso marcava os quatro círculos, e o que caía neles chegava do nada. Agora os
 * quatro são corpos autoritativos e viajam no canal de todos os corpos em voo:
 *
 * - **`cb: 'd'` é novo**: um obus. Entra por `pe` com o autor no boss, a posição
 *   e o passo do próximo tique; é atualizado por `pm` em todas as pulsações,
 *   porque pára no destino se o impacto tardar; e sai por `px` no tique em que o
 *   círculo dele cobra, ou quando o bombardeio acaba sem esse impacto.
 * - **`tx`/`ty` são novos**, e só com `cb: 'd'`: o centro do círculo onde ele
 *   cai. A cena não o pode deduzir — com o boss numa diagonal dos quatro
 *   círculos, dois obuses partem na mesma direcção para círculos diferentes.
 * - **`br` é o raio do círculo onde ele cai**, e não um raio de explosão: o obus
 *   não fere. O dano, a área, o alvo e o instante continuam a ser os do golpe
 *   `s` e da acção `strike`, exactamente como na 29.
 *
 * Um cliente construído contra 29 é recusado pelo aperto de mão: o validador
 * dele não conhece a letra nova.
 *
 * ## 31 — o tiro cresce com a nave (gate V28I.CF.3)
 *
 * Um tiro primário passa a nascer à escala física do casco que o disparou — a
 * mesma escala que a vida máxima dá ao casco, à hitbox e às bocas — e colide com
 * o raio-base multiplicado por ela. A escala fica congelada no disparo:
 *
 * - **`sc` é novo** em `pe` e em `pb`: a escala do corpo em basis points.
 *   **Ausente é `10 000`**, o tamanho-base, portanto uma trama de uma sala onde
 *   todos têm a vida-base é byte a byte a da 30. Num lote é comum a todos os
 *   corpos, que nasceram do mesmo casco no mesmo instante.
 * - Só um tiro primário de um casco o traz. Cartas, invocações, dardos, mísseis,
 *   bombas e corpos de boss continuam no tamanho que tinham.
 *
 * Um cliente construído contra 30 é recusado pelo aperto de mão: o validador
 * dele não conhece a letra nova, e desenharia um tiro de nave grande com o
 * tamanho de um tiro de novato.
 *
 * ## 32 — a salva viaja pela forma (gate V28I.CG, redesenho A)
 *
 * **`pv` é novo** na foto: uma salva a entrar na vista diz o corpo de referência
 * e a FORMA — as diferenças de identidade e de posição dos outros corpos, que
 * não mudam em voo —, definida uma vez por ligação e depois só referida. Os
 * corpos continuam discretos: cada um entra no mapa, é desenhado, sai por `px` e
 * morre sozinho. Os corpos de salva deixam de viajar em `pb` e em `pe`; cartas,
 * invocações, bombas e mísseis continuam onde estavam.
 *
 * Um cliente construído contra 31 é recusado pelo aperto de mão: o validador
 * dele recusaria a chave nova, e sem ela não veria nenhuma salva.
 */
export const OPEN_WORLD_ROOM_PROTOCOL_VERSION = 32;

/**
 * The largest command frame the room will even parse.
 *
 * Checked on the RAW message before `JSON.parse`, because parsing is the
 * expensive step and a megabyte of nonsense must cost a length check rather
 * than a parse. A legitimate command is well under 128 bytes.
 */
export const OPEN_WORLD_MAX_COMMAND_BYTES = 512;

/**
 * Commands accepted per second, per connection, before the room starts dropping.
 *
 * A 60 fps client that sends one command per frame sends 60; the allowance is
 * above that so an honest fast machine is never throttled, and far below what a
 * script can produce. Dropping is silent and counted - a rejected command is
 * not an error the player did anything about.
 */
export const OPEN_WORLD_MAX_COMMANDS_PER_SECOND = 90;

/**
 * **A cadência a que um cliente pode repetir uma intenção cuja única diferença
 * é a MIRA: sessenta por segundo** (gate V28I.AT).
 *
 * O limite acima foi derivado de "um cliente a 60 fps que envia um comando por
 * quadro envia 60". O cliente não cumpria essa premissa: ele emite uma intenção
 * por quadro do Phaser, que **não tem tecto de quadros**, e a mira é o vetor
 * NAVE → ponteiro — portanto ela muda a cada quadro enquanto a nave anda, mesmo
 * com o rato parado.
 *
 * Num monitor de 144 Hz isso são 144 comandos por segundo contra um teto de 90:
 * cinquenta e quatro descartes por segundo, e o socket fechado por
 * `rate_limit` ao fim de onze segundos. A 240 Hz, quatro segundos.
 *
 * ## Porque isto NÃO muda a jogabilidade
 *
 * A sala aplica a intenção e o tique lê-a; enviar mira mais depressa do que a
 * sala tica não muda o que ela usa. O intervalo é o da linha de base de 60 fps
 * de que o limite já derivava, portanto um jogador a 60 Hz emite exatamente o
 * que emitia, e um jogador a 144 Hz passa a emitir o mesmo que ele — nunca
 * menos. **Nenhum limite passa a depender dos quadros por segundo da máquina.**
 *
 * ## O que ele NÃO trava
 *
 * Uma TRANSIÇÃO — uma tecla de movimento, o começo ou o fim de um disparo —
 * viaja na hora, sem esperar por intervalo nenhum: essas são eventos e não
 * estado, e perder um toque de disparo seria mudar o jogo para poupar uma
 * mensagem. As ações discretas (cartas, saque, pesca, comerciante, travessia,
 * extração) não passam por aqui: são gestos do jogador e têm o seu próprio
 * caminho.
 *
 * ## O orçamento, somado
 *
 * `1000 / 16 = 62,5` de mira é o tecto do fluxo contínuo, a qualquer refrescamento:
 * medido, um monitor de 60 Hz envia 60, um de 144 Hz envia 48 e um de 360 Hz
 * envia 60 — nunca mais do que 62,5, e **zero descartes em todos**.
 *
 * Ao lado dele ficam as transições e as ações discretas. Um teclado a ser
 * martelado produz talvez vinte mudanças distintas de `mx`/`my`/`f` por
 * segundo, e um dedo rápido talvez oito cliques de painel — o que somaria
 * noventa e um contra noventa se as duas coisas acontecessem ao mesmo tempo.
 *
 * **Elas não acontecem ao mesmo tempo**, e a folga vem daí: enquanto um painel
 * de saque, de pesca ou do comerciante está aberto o ponteiro é dele, e uma
 * nave que não anda tem uma mira que não muda. Um segundo com sessenta e duas
 * miras é um segundo em que ninguém está a clicar em botões.
 *
 * E se ainda assim transbordar, o preço é uma atualização de mira perdida, em
 * silêncio: são precisos **seiscentos** descartes acumulados
 * (`OPEN_WORLD_RATE_LIMIT_CLOSE_THRESHOLD`) para um socket fechar, e a janela
 * de contagem reinicia a cada segundo. Um jogador não lá chega; um script chega
 * em menos de um segundo.
 */
export const OPEN_WORLD_CLIENT_AIM_HZ = 60;

/**
 * O tempo de um quadro dessa linha de base, arredondado para BAIXO: 16 ms.
 *
 * Para baixo de propósito. Um quadro a 60 Hz chega de 16,67 em 16,67 ms; um
 * intervalo de 17 recusaria um quadro sim, um quadro não, e um jogador a 60 Hz
 * passaria a enviar quarenta em vez de sessenta — exatamente a mira que este
 * gate não pode mudar. Com dezasseis, a linha de base passa inteira e só quem
 * está ACIMA dela é retido.
 */
export const OPEN_WORLD_MIN_AIM_INTERVAL_MS = Math.floor(1_000 / OPEN_WORLD_CLIENT_AIM_HZ);

/**
 * Sustained abuse, measured in dropped commands, before the socket is closed.
 *
 * One burst is a lag spike. Six hundred is a script, and a script that keeps a
 * socket open keeps a room's CPU busy for everybody else in the quadrant.
 */
export const OPEN_WORLD_RATE_LIMIT_CLOSE_THRESHOLD = 600;

/**
 * How much unsent data a connection may hold before the room stops adding to it.
 *
 * A client that opens a socket and never reads is not a protocol error - it is
 * a slow reader, and the runtime answers by queueing. A backlogged connection
 * is therefore skipped for the frames that are safe to skip: a `state` frame is
 * a photograph of a world the next one re-photographs 50 ms later.
 *
 * `welcome`, `error` and `closing` are NEVER skipped - each is said once and is
 * the only time it is said.
 *
 * What this deliberately does not do is change the tick. The simulation
 * advances identically for a backlogged room; throttling the TELLING is
 * presentation, throttling the world would be one player's slow connection
 * deciding everybody else's fight.
 */
export const OPEN_WORLD_MAX_BUFFERED_BYTES = 64 * 1024;

/**
 * The widest raw aim vector a client may send.
 *
 * The vector is normalised by the server, so its LENGTH carries no information
 * and only its direction survives. The bound exists so the normalisation cannot
 * be handed a value that overflows the integer maths - not because a longer
 * vector would aim further.
 */
export const OPEN_WORLD_MAX_AIM_COMPONENT = 1_000_000;

/**
 * How far ahead of the last APPLIED sequence a command may claim to be.
 *
 * The schema alone allows anything up to `Number.MAX_SAFE_INTEGER`, and a
 * client that sends that number once has permanently silenced itself: every
 * later command is at or below it and is dropped as a replay. That is only
 * self-harm, but it is self-harm the room can refuse instead of honour.
 *
 * Ten thousand is far past any honest gap - about two minutes of lost input at
 * the command ceiling above.
 */
export const OPEN_WORLD_MAX_SEQUENCE_SKIP = 10_000;

/**
 * The widest quadrant-local coordinate this protocol will parse, in milli-units.
 *
 * A bound on the SCHEMA, not on a quadrant: the room refuses anything outside
 * its own walls anyway, and this exists so a hostile value cannot reach the
 * integer maths at all. Generous enough that no quadrant this protocol
 * describes could outgrow it, tight enough that it is not just "a number".
 */
export const OPEN_WORLD_MAX_LOCAL_MU = 1_000_000_000;

/* -------------------------------------------------------------------------- */
/*  Client -> server                                                          */
/* -------------------------------------------------------------------------- */

/** One axis of the movement intent. Three values, and no fourth. */
const axisSchema = z.union([z.literal(-1), z.literal(0), z.literal(1)]);

const aimComponentSchema = z
  .number()
  .int()
  .min(-OPEN_WORLD_MAX_AIM_COMPONENT)
  .max(OPEN_WORLD_MAX_AIM_COMPONENT);

/**
 * The one command a playing client sends.
 *
 * `.strict()` like every authoritative request in this API: a field nobody
 * declared is a field nobody validated, and the first thing an attacker tries
 * is adding one.
 *
 * There is no card order, no channel, no fishing, no mining, no salvage and no
 * traversal in it, because none of those exist in this gate. Each will be a
 * field somebody adds deliberately, with its own rules - never a field that was
 * left open in advance.
 */
export const openWorldCommandSchema = z
  .object({
    t: z.literal('cmd'),
    /**
     * Strictly increasing, per connection.
     *
     * The room keeps the highest it has seen on this connection and drops
     * anything at or below it. That is what makes a retry harmless and a replay
     * useless: an old frame cannot walk a hull backwards, because an old frame
     * is not applied.
     *
     * Per CONNECTION and not per account: a reconnection is a new socket with a
     * new counter, and the room says so by minting a new generation for the
     * chair. A frame from a superseded generation is not this player speaking,
     * whatever its sequence claims.
     */
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    /** WASD reduced to two axes. Not a speed - the room owns speed. */
    mx: axisSchema,
    my: axisSchema,
    /** Where the player is pointing. Direction only; the room normalises it. */
    ax: aimComponentSchema,
    ay: aimComponentSchema,
    /** Is the fire button held. Cadence is the room's, not the client's. */
    f: z.boolean(),
    /**
     * **The order to DETONATE this pilot's bleeding marks.**
     *
     * ## What it may say, and that is all
     *
     * That the button was pressed. There is no target, no count, no magnitude
     * and no list - the authority decides on its own what bursts, whose, on
     * whom and for how much, from state the client does not write. A target
     * field here would be the first thing an attacker tried; there is none.
     *
     * ## Why an optional boolean and not a counter
     *
     * Because it is a LATCH and not a sustained state: the room arms the order
     * and the next tick spends it. Most frames do not carry it, and an absent
     * field says "I asked for nothing".
     *
     * With no marks it is a safe no-op: nothing happens, nothing is spent and
     * nothing is announced.
     */
    bd: z.boolean().optional(),
  })
  .strict();

export type OpenWorldCommand = z.infer<typeof openWorldCommandSchema>;

/**
 * Everything a client may say. One frame today, and a union so the second one
 * arrives as a discriminated case rather than as an optional field.
 */
export const openWorldClientFrameSchema = z.discriminatedUnion('t', [
  openWorldCommandSchema,
  /**
   * **The crossing intent** (version 3).
   *
   * A frame of its own rather than a field on the command, and the difference
   * is not cosmetic: a command is a photograph of which keys are down, sent
   * sixty times a second and safe to lose. A crossing is an ORDER, sent once,
   * carrying its own identity so a retry is read instead of obeyed twice. Two
   * different lifetimes should not share a shape.
   */
  openWorldCrossingIntentSchema,
  /**
   * **The five loot intents** (version 5).
   *
   * Five frames rather than one with a mode field, for the same reason a
   * crossing is not a field on the command: each carries exactly what its own
   * action needs and nothing else, so there is nowhere to put a quantity on a
   * close and nowhere to put an item id on a take-all.
   *
   * None of them carries who the player is or where they are. Both come from
   * the socket, which is what makes "the client cannot choose the range it can
   * loot from" a property of the schema rather than of a check.
   */
  openWorldLootOpenSchema,
  openWorldLootTakeSchema,
  openWorldLootTakeAllSchema,
  openWorldLootGoldSchema,
  openWorldLootCloseSchema,
  /**
   * **The three border intents** (version 6).
   *
   * Entering a port, preparing an extraction, and calling either of them off.
   * Travelling to a neighbouring dangerous quadrant is NOT here: it stays the
   * ordinary crossing intent, because an ordinary crossing is what it is and it
   * costs no ten seconds.
   *
   * None of them says that a channel finished, how long it has run, or that the
   * hull is in the strip. All three are the server's, on the server's clock.
   */
  openWorldPortEntrySchema,
  openWorldExtractionStartSchema,
  openWorldBorderCancelSchema,
  /**
   * **Comecar a pescar** (versao 13). Uma intencao com identidade.
   *
   * Nao nomeia agua, peixe, especie, raridade, peso nem dano: o servidor
   * deriva a agua da posicao autoritativa e escolhe o peixe
   * deterministicamente.
   */
  openWorldFishStartSchema,
  /**
   * **Abandonar** (versao 20). Com o id da tentativa, e nao sem id.
   *
   * Ate a versao 19 chamava-se cancelar e nao decidia nada: o peixe voltava
   * inteiro a agua. Agora ele escapa, portanto a ordem tem de dizer QUAL
   * tentativa abandona.
   */
  openWorldFishAbandonSchema,
  /** **Pedir a lista da propria carga** (versao 15). Nao decide nada. */
  openWorldCargoInspectSchema,
  /**
   * **Abrir o balcao** (versao 14). Pede as duas listas vendaveis.
   *
   * Sem id de ordem: perguntar nao decide nada, e repetir e o mesmo.
   */
  openWorldMerchantOpenSchema,
  /**
   * **Vender** (versao 14). Uma intencao com identidade.
   *
   * Nao leva preco, raridade, peso, multiplicador nem quantidade. Um
   * "vender todos" nao leva sequer a lista: o servidor determina outra vez
   * quais as linhas elegiveis daquela seccao.
   */
  openWorldMerchantSellSchema,
  /**
   * **As três intenções das cartas** (versao 16).
   *
   * Ligar, soltar a carga, e pedir para pilotar. Cada uma leva o espaco e a
   * propria identidade, e nada mais: nao ha campo para dono, nivel,
   * arrefecimento, alvo, duracao de carga, dano nem resultado.
   */
  openWorldCardActivateSchema,
  openWorldCardReleaseSchema,
  openWorldCardBoardSchema,
]);

export type OpenWorldClientFrame = z.infer<typeof openWorldClientFrameSchema>;

/* -------------------------------------------------------------------------- */
/*  Server -> client                                                          */
/* -------------------------------------------------------------------------- */

/** An entity's id inside one room. Opaque, and never an account. */
export const openWorldEntityIdSchema = z.number().int().min(1);

const localCoordinateSchema = z.number().int().min(0).max(OPEN_WORLD_MAX_LOCAL_MU);

const signedLocalSchema = z
  .number()
  .int()
  .min(-OPEN_WORLD_MAX_LOCAL_MU)
  .max(OPEN_WORLD_MAX_LOCAL_MU);

/**
 * What a hull is doing, right now, as the authority sees it.
 *
 * The same shape whether the hull is entering a view or merely being updated
 * inside one, so a client has one decoder rather than two that must agree.
 */
export const openWorldEntityStateSchema = z
  .object({
    i: openWorldEntityIdSchema,
    /** Quadrant-LOCAL milli-units. There is no world coordinate, by decision. */
    x: localCoordinateSchema,
    y: localCoordinateSchema,
    /** The movement intent the authority applied, for a client's smoothing. */
    mx: axisSchema,
    my: axisSchema,
    /** The NORMALISED aim the authority holds. Never the raw vector sent. */
    ax: signedLocalSchema,
    ay: signedLocalSchema,
    /** Health in milli, as a decimal string. Progression has no ceiling. */
    h: pvpHealthMilliSchema,
    /** Is somebody at the controls. A dropped hull stays, and stops answering. */
    c: z.boolean(),
    /**
     * Where this hull's blades are on their orbit, when it has any.
     *
     * The authoritative phase, in the units the orbit is counted in. Absent
     * means no blades - which is every hull without the jewel, and therefore
     * most frames.
     *
     * The phase and not the angle: an angle is a rendering of it, and sending a
     * rendering would let the drawing and the collision disagree about where a
     * blade is.
     */
    sp: z.number().int().min(0).optional(),
    /**
     * **As bandeiras publicas das cartas** (versao 16).
     *
     * O que esta a acontecer a este casco e que quem esta do outro lado tem de
     * ver: congelado, inalvejavel, infetado, paralisado, lento, em
     * curto-circuito, marcado, e quanta casca de gelo desenhar. Nunca **quanto
     * falta** de nada: isso e informacao de decisao e viaja na metade privada.
     */
    ...openWorldCardFlagsSchema.shape,
    /** O que as cartas deste casco estao a DESENHAR. Ausente = nada. */
    cv: openWorldCardVisualSchema.optional(),
    /**
     * **Quanto da carga ja foi cumprido**, em por cento.
     *
     * Presente so enquanto `c` ou `g` estao na lista. E PUBLICO de proposito, e
     * e a excecao deliberada a regra de que "quanto falta" e privado: a Lanca
     * Solar prende quem a carrega, e o contrato aprovado diz que quem esta a
     * frente ve a nave presa e tem tempo para sair da linha.
     */
    cg: z.number().int().min(0).max(100).optional(),
    /** Com que forca o feixe esta a arder, em por cento. So com `l` na lista. */
    cw: z.number().int().min(0).max(100).optional(),
    /** Os dois canhoes temporarios estao montados. Ausente = nao. */
    sc: z.literal(1).optional(),
  })
  .strict();

export type OpenWorldEntityState = z.infer<typeof openWorldEntityStateSchema>;

/**
 * A hull ENTERING a viewer's interest area: everything that does not change.
 *
 * Sent once, on entry, so the twenty frames a second that follow carry the
 * state alone. A hull that leaves and comes back is announced again - which is
 * exactly why the interest area has hysteresis, so a hull hovering on the edge
 * does not pay this every other frame.
 */
export const openWorldEntityEntrySchema = z
  .object({
    s: openWorldEntityStateSchema,
    /** The player's label, or null. A hull without one flies just the same. */
    n: pvpNicknameSchema.nullable(),
    /** Ship, skin and cannon. The fleet's one appearance contract. */
    a: pvpAppearanceSchema,
    /** This hull's maximum, in milli. The denominator of its bar. */
    hm: pvpHealthMilliSchema,
    /** The AUTHORITATIVE radius in milli-units. The client draws this one. */
    r: z.number().int().min(1).max(OPEN_WORLD_MAX_LOCAL_MU),
    /**
     * How many blades orbit this hull. Absent means none.
     *
     * On the ENTRY and not on the state, because it cannot change while a chair
     * is held: the profile is frozen when the pilot sits down, and the blade
     * count is a function of it. Sending it twenty times a second would be
     * paying for a number that never moves.
     *
     * The FACT travels and the reason does not: a viewer is told a hull has
     * four blades, and never which jewel, at what level or with how many
     * copies - that lives in the frozen profile, which never leaves the server.
     */
    sb: z.number().int().min(1).max(16).optional(),
    /**
     * **Este casco esta na MINHA composicao congelada** (versao 18).
     *
     * Por espetador e nao por casco: a mesma nave e aliada de uns e inimiga de
     * outros, e a sala responde a pergunta uma vez por ligacao. Ausente quer
     * dizer "nao", que e o estado de quase todo o casco de quase toda a vista.
     *
     * O que viaja e o FACTO, e nunca a razao: nem a conta, nem a expedicao, nem
     * o digest. Um espetador aprende que aquela nave voa com ele, e nada sobre
     * quem ela e.
     */
    al: z.literal(1).optional(),
  })
  .strict();

export type OpenWorldEntityEntry = z.infer<typeof openWorldEntityEntrySchema>;

/**
 * **What kind of thing is flying.**
 *
 * Three, and a closed list. A bomb and a missile are drawn differently, have
 * different bodies and end differently, and a viewer that could not tell them
 * from an ordinary shot would be watching a fight it cannot read.
 *
 * It is a KIND and never a jewel: the wire says "this is a bomb", not "the
 * Atomic Core at level 30 produced this".
 */
export const OPEN_WORLD_PROJECTILE_KINDS = ['shot', 'bomb', 'missile'] as const;
export type OpenWorldProjectileKind = (typeof OPEN_WORLD_PROJECTILE_KINDS)[number];

/**
 * **O maior lote que uma forma admite.**
 *
 * Derivado e não escolhido: o maior nascimento simultâneo de uma família
 * elegível é uma salva primária, e a mais larga que o catálogo permite são as
 * sete bocas do nível máximo vezes os projéteis do canhão mais largo. O limite
 * existe para uma trama malformada ser recusada por comparação, e não para
 * limitar o jogo — nenhum acontecimento legal lá chega.
 *
 * Valia vinte e oito enquanto um quarto canhão de quatro projéteis estava
 * planeado. Essa atualização foi cancelada (ADR 0132) e o número **desceu
 * sozinho** para vinte e um, porque sai do catálogo em vez de estar escrito
 * aqui. Nenhuma trama legal alguma vez carregou mais de vinte e uma: o quarto
 * canhão nunca foi equipável.
 */
/**
 * **A escala de um corpo que a trama não diz** (versão 31, gate V28I.CF.3).
 *
 * `10 000` basis points é o tamanho-base, e é o que a ausência de `sc` significa
 * numa entrada e num lote. A mesma identidade da lei de escala do casco: um
 * casco com a vida-base dispara tiros deste tamanho.
 */
export const OPEN_WORLD_BASE_PROJECTILE_SCALE_BPS = 10_000;

export const OPEN_WORLD_PROJECTILE_BATCH_MAX =
  (ITEM_LEVEL_CAPS.cannon ?? 1) * MAX_PROJECTILES_PER_CANNON;

/** A projectile entering a view: where it is, where it is going, and whose. */
export const openWorldProjectileEntrySchema = z
  .object({
    i: z.number().int().min(1),
    /** The entity that fired it. A viewer may not know that entity; that is fine. */
    o: openWorldEntityIdSchema,
    /** Absent means `shot`, which is almost every projectile in almost every frame. */
    k: z.enum(OPEN_WORLD_PROJECTILE_KINDS).optional(),
    x: localCoordinateSchema,
    y: localCoordinateSchema,
    /** Milli-units per tick. Constant for a shot, re-sent for a missile. */
    vx: signedLocalSchema,
    vy: signedLocalSchema,
    /**
     * **Este e o corpo de uma CARTA, e qual** (versao 16).
     *
     * `f` a Esfera Ignea, `c` o Catole, `z` a esfera do Congelamento, `t` a
     * estrela da Lentidao e `s` o raio do Curto-Circuito. Ausente e um tiro
     * comum ou uma Bomba Atomica, que continua a marcar-se com `k`.
     *
     * Viaja porque a cena nao o pode deduzir: as cinco coisas desenham-se de
     * maneiras diferentes, e adivinha-lo pelo TAMANHO seria a cena a decidir a
     * arte a partir de um numero de equilibrio.
     */
    cb: z
      .union([
        z.literal('f'),
        z.literal('c'),
        z.literal('z'),
        z.literal('t'),
        z.literal('s'),
        /** Uma esfera da Postura de Enxame (versao 27). Nunca uma carta. */
        z.literal('w'),
        /** Uma bala do Fogo Rotativo (versao 28). Um tiro real, e nunca uma carta. */
        z.literal('g'),
        /** Um obus do Bombardeio de Profundidade (versao 30). Nunca fere: o circulo e que cobra. */
        z.literal('d'),
      ])
      .optional(),
    /**
     * **O raio da explosao que ele vai fazer**, em milli-unidades.
     *
     * Presente so com `cb`, e existe por uma razao que a Bomba nao tem: a
     * Esfera Ignea CRESCE com a carga. Um corpo de tamanho fixo mostraria a
     * mesma bola para um toque e para uma carga cheia. Nunca carrega dano,
     * nivel, carta nem dono.
     */
    br: z.number().int().min(0).optional(),
    /**
     * **A escala física do corpo, em basis points** (versão 31, gate V28I.CF.3).
     *
     * Um tiro primário nasce à escala do casco que o disparou e colide com o
     * raio-base multiplicado por ela; a cena desenha-o pelo mesmo número.
     * Congelada no disparo. **Ausente é `10 000`**, o tamanho-base — que é o
     * de quase todo o corpo de quase todo o tique, e o de tudo o que não é um
     * tiro primário de um casco.
     */
    sc: z.number().int().min(0).optional(),
    /**
     * **Onde este obus cai** (versao 30): o centro do circulo do Bombardeio de
     * Profundidade que ele representa, em milli-unidades.
     *
     * Presente so com `cb: 'd'`. Viaja porque a cena nao o pode deduzir: com o
     * boss alinhado numa diagonal dos quatro circulos, dois obuses partem na
     * mesma direccao para circulos diferentes. E um dos pontos que o golpe `s`
     * ja publica, e nao carrega dano, tique nem alvo.
     */
    tx: localCoordinateSchema.optional(),
    ty: localCoordinateSchema.optional(),
    /**
     * **Este corpo voa a DIREITO, e por isso não vai receber atualizações**
     * (versão 22).
     *
     * Presente quer dizer exatamente uma coisa: a posição deste corpo é
     * `x + vx x (tique - tique_da_entrada)`, um passo por tique autoritativo,
     * até o `px` chegar. Ausente é o significado de sempre — "espera pelo
     * `pu`" — e é por isso que uma variante antiga **não** passa a ser linear
     * por omissão.
     *
     * ## Porque a marca é explícita
     *
     * Porque a ausência de um campo não pode significar duas coisas. Sem esta
     * marca, "não recebi `pu`" seria indistinguível de "a rede atrasou-se", e um
     * cliente teria de adivinhar qual das duas — que é a única maneira de este
     * mecanismo poder mentir.
     *
     * ## Quem NÃO a tem
     *
     * Tudo o que possa curvar, acelerar, mudar de rumo ou ser corrigido: um
     * míssil, um Catolé, um dardo teleguiado, e qualquer corpo cuja velocidade a
     * sala possa alterar. Esses continuam a receber `pu` em todas as tramas,
     * exatamente como antes.
     *
     * ## O que ela NÃO autoriza
     *
     * O cliente continua a não decidir **nada**: nem acerto, nem colisão, nem
     * dano, nem morte, nem expiração, nem remoção. Um corpo linear só desaparece
     * quando a sala publicar o `px`, e a explosão acontece no ponto do evento
     * autoritativo — nunca na posição que a apresentação calculou.
     */
    l: z.literal(1).optional(),
  })
  .strict();

export type OpenWorldProjectileEntry = z.infer<typeof openWorldProjectileEntrySchema>;

/* -------------------------------------------------------------------------- */
/*  O nascimento em LOTE                                                      */
/* -------------------------------------------------------------------------- */

/**
 * **Uma salva inteira, dita uma vez** (versão 23, gate V28I.AI).
 *
 * ## O que ela é, e o que não é
 *
 * É **transporte, e só transporte**. Vinte e oito corpos de uma salva Prismatic
 * partilham o dono, o rumo, o tipo e a marca de linearidade, e a forma `pe`
 * repetia esses campos vinte e oito vezes. Aqui eles são ditos uma vez, e o que
 * varia — a identidade e o ponto de nascimento — viaja num tuplo por corpo.
 *
 * Depois de expandido, o cliente tem **exatamente os mesmos corpos individuais**
 * que teria recebido em vinte e oito `pe`: as mesmas identidades, as mesmas
 * posições, as mesmas velocidades, as mesmas marcas. Nada é aproximado, nada é
 * quantizado e nada é deduzido.
 *
 * **Não é uma agregação.** O servidor continua com vinte e oito entidades, vinte
 * e oito colisões e vinte e oito autorias; a cena continua a desenhar vinte e
 * oito sprites, vinte e oito rastos e vinte e oito impactos. Um projétil
 * representativo, um dano multiplicado ou um corpo a menos continuam proibidos.
 *
 * ## Porque `b` tem pelo menos dois
 *
 * Porque um lote de um corpo é maior do que o `pe` que ele substituiria. A
 * forma recusa-o em vez de o permitir e confiar em quem a constrói.
 *
 * ## O que ela NÃO carrega
 *
 * Nem dano, nem vida, nem carta, nem joia, nem conta, nem a dimensão original da
 * salva. `b` tem exatamente os corpos que **aquela ligação já ia receber** — um
 * lote é montado depois da área de interesse e depois do Manto, nunca antes.
 * Uma otimização não pode ampliar informação.
 */
export const openWorldProjectileBatchSchema = z
  .object({
    /** O dono, dito uma vez. Comum a todo o lote por construção. */
    o: openWorldEntityIdSchema,
    /** Ausente é `shot`, exatamente como no `pe` individual. */
    k: z.enum(OPEN_WORLD_PROJECTILE_KINDS).optional(),
    /** O rumo, em milli-unidades por tique. Comum, ou não haveria lote. */
    vx: signedLocalSchema,
    vy: signedLocalSchema,
    /** O corpo de carta, quando os há. Comum a todo o lote. */
    cb: z
      .union([
        z.literal('f'),
        z.literal('c'),
        z.literal('z'),
        z.literal('t'),
        z.literal('s'),
        /** Uma esfera da Postura de Enxame (versao 27). Nunca uma carta. */
        z.literal('w'),
        /** Uma bala do Fogo Rotativo (versao 28). Um tiro real, e nunca uma carta. */
        z.literal('g'),
        /** Um obus do Bombardeio de Profundidade (versao 30). Nunca fere: o circulo e que cobra. */
        z.literal('d'),
      ])
      .optional(),
    /** O raio da explosão, quando existe. Comum. */
    br: z.number().int().min(0).optional(),
    /** A escala do corpo, quando não é a base (versão 31). Comum a todo o lote. */
    sc: z.number().int().min(0).optional(),
    /** A marca de linearidade do gate V28I.Z.3. Comum. */
    l: z.literal(1).optional(),
    /**
     * **Um corpo: identidade, x, y.** É tudo o que varia dentro de um lote.
     *
     * Um tuplo e não um objeto, porque três números com nome repetido vinte e
     * oito vezes são exatamente a repetição que esta forma existe para tirar.
     *
     * Identidades repetidas são **recusadas**: duas entradas com o mesmo `i`
     * seriam dois corpos que a cena não consegue distinguir, e expandir metade
     * de um lote é pior do que recusá-lo inteiro.
     */
    b: z
      .array(z.tuple([z.number().int().min(1), localCoordinateSchema, localCoordinateSchema]))
      .min(2)
      .max(OPEN_WORLD_PROJECTILE_BATCH_MAX)
      .refine(
        (bodies) => new Set(bodies.map(([id]) => id)).size === bodies.length,
        'a projectile batch may not repeat an id',
      ),
  })
  .strict();

export type OpenWorldProjectileBatch = z.infer<typeof openWorldProjectileBatchSchema>;

/**
 * **A expansão, e ela vive aqui para existir uma vez só.**
 *
 * O cliente chama-a para recuperar as entradas individuais; uma auditoria chama-a
 * para provar que `expandir(agrupar(x)) === x`. Duas implementações — uma no
 * servidor e outra no navegador — seriam duas respostas à mesma pergunta, e a
 * primeira a divergir seria aquela em que ninguém tocou.
 *
 * Pura, determinística e **na ordem em que os corpos vieram**: a ordem canónica
 * das bocas é a ordem do array, e reordenar aqui mudaria a sequência de
 * identidades que a cena regista.
 */
export function openWorldExpandProjectileBatch(
  batch: OpenWorldProjectileBatch,
): OpenWorldProjectileEntry[] {
  const entries: OpenWorldProjectileEntry[] = [];
  openWorldForEachProjectileInBatch(batch, (i, x, y, vx, vy, k, o, cb, br, l) => {
    entries.push({
      i,
      o,
      ...(k === undefined ? {} : { k }),
      x,
      y,
      vx,
      vy,
      ...(cb === undefined ? {} : { cb }),
      ...(br === undefined ? {} : { br }),
      ...(batch.sc === undefined ? {} : { sc: batch.sc }),
      ...(l ? { l: 1 as const } : {}),
    });
  });
  return entries;
}
/* -------------------------------------------------------------------------- */
/*  A atualização em LOTE                                                     */
/* -------------------------------------------------------------------------- */

/**
 * **Quantas atualizações uma trama pode carregar** (gate V28I.AP).
 *
 * Um corpo que curva ocupa aqui um lugar por pulsação enquanto estiver à vista.
 * O número não é um limite de jogo: é o teto que faz uma trama adulterada ser
 * recusada em vez de aceite, e está uma ordem de grandeza acima do que a
 * lotação e a área de interesse conseguem produzir.
 *
 * A conta, com as constantes canónicas: um casco no tecto dos artefactos dispara
 * `30,72` salvas por segundo, um míssil sai em `10 %` delas
 * (`MISSILE_MAX_CHANCE_BPS`) e vive nove segundos
 * (`PVP_MISSILE_LIFETIME_TICKS`), o que dá cerca de vinte e oito mísseis vivos
 * por piloto. Nem todos são visíveis de uma vez, e mesmo que o fossem
 * precisaria de mais de duzentos pilotos num quadrante para lá chegar.
 */
export const OPEN_WORLD_PROJECTILE_MOVE_MAX = 8_192;

/**
 * **Um corpo que se moveu: `[i, x, y, vx, vy]`.**
 *
 * Um tuplo e não um objeto, pela mesma razão do lote de nascimento: `pu`
 * repetia `"i"`, `"x"`, `"y"`, `"vx"` e `"vy"` uma vez por corpo e por
 * pulsação, e essas cinco chaves são vinte e quatro bytes que nunca mudam.
 *
 * **Os cinco valores estão sempre presentes, e não é uma escolha desta forma:**
 * `pu` só recebe corpos que CURVAM desde o gate V28I.Z.3, e um corpo que curva
 * manda sempre o rumo junto com a posição. A forma antiga tinha `vx` e `vy`
 * opcionais para uma variante que a sala deixou de produzir.
 *
 * O tuplo é validado posição a posição — identidade, duas coordenadas locais e
 * dois deslocamentos com sinal — portanto um comprimento errado, um tipo errado
 * ou um número fora do intervalo são recusados pela forma e não por uma
 * verificação que alguém se lembre de escrever.
 */
export const openWorldProjectileMoveSchema = z.tuple([
  z.number().int().min(1),
  localCoordinateSchema,
  localCoordinateSchema,
  signedLocalSchema,
  signedLocalSchema,
]);

export type OpenWorldProjectileMove = z.infer<typeof openWorldProjectileMoveSchema>;

/**
 * **A expansão de uma atualização em lote**, e vive aqui para existir uma vez.
 *
 * Dá **exatamente** o objeto `pu` que teria viajado: mesma identidade, mesma
 * posição, mesmo rumo. Uma auditoria usa-a para provar `expandir(agrupar(x)) === x`.
 *
 * O cliente **não** precisa dela no caminho quente — ele lê o tuplo e escreve
 * direto no corpo persistente, sem construir o objeto intermédio — e é por isso
 * que ela existe sem que ninguém a chame vinte vezes por segundo.
 */
export function openWorldExpandProjectileMove(
  move: OpenWorldProjectileMove,
): OpenWorldProjectileState {
  const [i, x, y, vx, vy] = move;
  return { i, x, y, vx, vy };
}

/**
 * **Percorrer um lote de nascimento sem o materializar** (gate V28I.AP).
 *
 * {@link openWorldExpandProjectileBatch} constrói um array com um objeto por
 * corpo, e o cliente deitava-o fora no mesmo passo em que o lia: vinte e oito
 * objetos e um array por lote, por trama. Esta função entrega os mesmos campos
 * pela mesma ordem sem alocar nenhum deles.
 *
 * A fórmula continua a ser **uma**: a expansão em array é escrita em cima desta,
 * portanto não há duas respostas à mesma pergunta.
 */
export function openWorldForEachProjectileInBatch(
  batch: OpenWorldProjectileBatch,
  visit: (
    id: number,
    x: number,
    y: number,
    vx: number,
    vy: number,
    kind: OpenWorldProjectileKind | undefined,
    owner: number,
    card: OpenWorldProjectileEntry['cb'],
    blastMu: number | undefined,
    linear: boolean,
  ) => void,
): void {
  const linear = batch.l === 1;
  for (const [id, x, y] of batch.b) {
    visit(id, x, y, batch.vx, batch.vy, batch.k, batch.o, batch.cb, batch.br, linear);
  }
}

/**
 * **Quantas formas de salva uma ligação guarda** (versão 32). Chegado o teto, a
 * sala esquece a tabela inteira e volta a definir cada forma na primeira vez que
 * a usa: nenhuma forma sobrevive a um esquecimento do outro lado.
 */
export const OPEN_WORLD_VOLLEY_SHAPES_MAX = 64;

/**
 * **Uma salva a entrar na vista** (versão 32, gate V28I.CG, redesenho A).
 *
 * Os corpos continuam a ser corpos: cada um tem a sua identidade, entra no mapa
 * do cliente, é desenhado, sai por \`px\` e morre sozinho. O que muda é o
 * transporte das suas posições. Uma salva é um grupo de corpos paralelos cujas
 * DIFERENÇAS entre si — de identidade e de posição — não mudam em voo e são as
 * mesmas de uma salva para a seguinte enquanto a mira, o canhão e a escala não
 * mudam. Essas diferenças são a FORMA; viajam uma vez por ligação e as salvas
 * seguintes só dizem qual.
 *
 * - \`r\`: o corpo de referência — o de menor identidade dos que entram —, com a
 *   posição dele NESTE tique.
 * - \`s\`: a forma. Cada tripla \`[Δid, Δx, Δy]\` da forma é outro corpo a entrar:
 *   identidade \`r.id + Δid\`, posição \`r + (Δx, Δy)\`. Inteiros exatos,
 *   calculados pela sala a partir das posições autoritativas: o cliente soma, e
 *   nunca refaz geometria.
 * - \`d\`: a definição da forma \`s\`, quando é a primeira vez que esta ligação a
 *   usa (ou a primeira depois de um esquecimento). Substitui o que \`s\` fosse.
 *
 * Um corpo de salva voa a direito: é sempre um \`shot\` linear (\`l\`), sem carta,
 * sem explosão e sem perseguição. O resto do contrato é o de \`pb\`.
 */
export const openWorldProjectileVolleySchema = z
  .object({
    o: openWorldEntityIdSchema,
    vx: signedLocalSchema,
    vy: signedLocalSchema,
    /** A escala do corpo, quando não é a base. Comum. */
    sc: z.number().int().min(0).optional(),
    s: z
      .number()
      .int()
      .min(0)
      .max(OPEN_WORLD_VOLLEY_SHAPES_MAX - 1),
    r: z.tuple([z.number().int().min(1), localCoordinateSchema, localCoordinateSchema]),
    d: z
      .array(z.number().int())
      .max(3 * (OPEN_WORLD_PROJECTILE_BATCH_MAX - 1))
      .refine((flat) => flat.length % 3 === 0, 'a volley shape is a list of triples')
      .refine((flat) => {
        for (let index = 0; index < flat.length; index += 3) {
          if ((flat[index] ?? 0) < 1) {
            return false;
          }
        }
        return true;
      }, 'a volley shape only reaches bodies after the reference')
      .optional(),
  })
  .strict();

export type OpenWorldProjectileVolley = z.infer<typeof openWorldProjectileVolleySchema>;

/**
 * **A expansão canónica de uma salva**, a única: o cliente e qualquer
 * descodificador de referência chamam esta, e nenhum escreve a sua. Define a
 * forma quando \`d\` vem, e visita cada corpo pela ordem de identidade — a
 * referência primeiro. Uma forma que a ligação não conhece é um erro do
 * transporte, dito alto: desenhar à sorte seria pior.
 */
export function openWorldForEachProjectileInVolley(
  volley: OpenWorldProjectileVolley,
  shapes: Map<number, readonly number[]>,
  visit: (id: number, x: number, y: number) => void,
): void {
  if (volley.d !== undefined) {
    shapes.set(volley.s, volley.d);
  }
  const shape = shapes.get(volley.s);
  if (shape === undefined) {
    throw new Error(`a volley referenced shape ${volley.s} before it was defined`);
  }
  const [id, x, y] = volley.r;
  visit(id, x, y);
  for (let index = 0; index + 2 < shape.length; index += 3) {
    visit(id + (shape[index] ?? 0), x + (shape[index + 1] ?? 0), y + (shape[index + 2] ?? 0));
  }
}

/**
 * A projectile already known to this viewer: where it got to, and where it is
 * going if that changed.
 *
 * An ordinary shot and a bomb travel in a straight line, so their velocity is
 * said once on entry and never again. A missile STEERS, so its heading is part
 * of what changed this tick and travels with the position - without it a client
 * would draw it pointing where it was aimed when it was born.
 */
export const openWorldProjectileStateSchema = z
  .object({
    i: z.number().int().min(1),
    x: localCoordinateSchema,
    y: localCoordinateSchema,
    vx: signedLocalSchema.optional(),
    vy: signedLocalSchema.optional(),
  })
  .strict();

export type OpenWorldProjectileState = z.infer<typeof openWorldProjectileStateSchema>;

/** How a hull ended. There is no reward and no container in this gate. */
export const OPEN_WORLD_DEATH_CAUSES = ['combat', 'abandoned'] as const;
export type OpenWorldDeathCause = (typeof OPEN_WORLD_DEATH_CAUSES)[number];

/**
 * A hull was destroyed, said once.
 *
 * An authoritative FACT, and nothing more: no loot, no container, no gold, no
 * experience and no item. What replaces a wreck is a later gate's decision, and
 * inventing a field for it here would be deciding it early.
 */
export const openWorldDeathSchema = z
  .object({
    i: openWorldEntityIdSchema,
    /** The tick it happened on, never the tick this frame was published on. */
    k: z.number().int().min(0),
    x: localCoordinateSchema,
    y: localCoordinateSchema,
    c: z.enum(OPEN_WORLD_DEATH_CAUSES),
  })
  .strict();

export type OpenWorldDeath = z.infer<typeof openWorldDeathSchema>;

/**
 * **The closed list of what a jewel can announce.**
 *
 * Seven ids, and they are the game's - `damage/origin.ts` owns them, the duel
 * uses the same ones, and this list is the wire's copy of that closed table
 * rather than a second vocabulary. `shared` cannot import `game-domain` - that
 * would be a loop in the dependency direction - so the equality is asserted on
 * the domain's side, exactly as `PVP_MAX_WIRE_SAW_BLADES` already is.
 */
export const OPEN_WORLD_JEWEL_EFFECT_IDS = [
  'lifesteal_heal',
  'cyclone_cut',
  'atomic_blast',
  'missile',
  'bleed_detonation',
  'reflection',
  'vital_heal',
] as const;

export type OpenWorldJewelEffectId = (typeof OPEN_WORLD_JEWEL_EFFECT_IDS)[number];

/**
 * **Something a jewel did, said once.**
 *
 * One channel for all seven, rather than the duel's four. A quadrant has no
 * renderer yet, and one well-typed event that carries who, whom, where, how
 * wide and how much is what a future one needs - four schemas would be four
 * things to keep in step for a scene nobody has written.
 *
 * ## What it never carries
 *
 * A jewel's name, its level, how many copies produced it, the chance it rolled
 * against, or any art parameter. All of those live in the frozen profile, which
 * never leaves the server: a viewer learns that a hull was cut, and never how
 * good the thing that cut it is.
 *
 * ## And it never reveals somebody who is not in view
 *
 * An event is delivered to a connection only when its position is inside that
 * connection's interest area AND every hull it names is either the viewer or
 * already visible to it. An event that would name a stranger is DROPPED rather
 * than trimmed - trimming would still have said that something happened over
 * there.
 */
export const openWorldJewelEventSchema = z
  .object({
    e: z.enum(OPEN_WORLD_JEWEL_EFFECT_IDS),
    /** The tick it happened on, never the tick this frame was published on. */
    k: z.number().int().min(0),
    /** Who caused it. On a reflection this is the reflector: the payload turned. */
    p: openWorldEntityIdSchema,
    /** Who received it, or null when the effect names nobody. */
    q: openWorldEntityIdSchema.nullable(),
    x: localCoordinateSchema,
    y: localCoordinateSchema,
    /** The MECHANICAL radius. Zero for anything that is not an area. */
    r: z.number().int().min(0).max(OPEN_WORLD_MAX_LOCAL_MU),
    /**
     * What was actually applied or healed, in milli, as a decimal string.
     *
     * Never the magnitude the effect was WORTH: the clamp already happened, and
     * a number that ignored it would print more than the bar moved. Null when
     * there is nothing to report yet - a bomb that has only just detonated, or
     * a payload that has been turned and has not landed.
     */
    n: pvpHealthMilliSchema.nullable(),
  })
  .strict();

export type OpenWorldJewelEvent = z.infer<typeof openWorldJewelEventSchema>;

/**
 * **Where one ally is, and nothing else about them.**
 *
 * ## What a teammate may know, and where the line is
 *
 * Which quadrant they are in, whether somebody is at the controls, whether they
 * are still alive, and roughly which way to look when they are elsewhere. That
 * is enough for a marker on a future compass and stops exactly there.
 *
 * It carries **no life, no cargo, no inventory, no nearby enemies and no
 * combat** from another quadrant. A party is not a way to see through walls: a
 * member in `C/5` learns that their ally is in `C/6`, not what is happening to
 * them there.
 *
 * ## Why a direction and not a position
 *
 * Because a position in another quadrant is information about that quadrant.
 * "East of here" is what a compass needs; a coordinate pair would let a party
 * relay an ally's exact whereabouts to somebody hunting them.
 *
 * ## Who receives it
 *
 * Only connections whose own account is in the same frozen composition. It
 * names accounts, and that is safe by construction: the composition they are
 * both in already lists every one of them.
 */
export const openWorldAllyStateSchema = z
  .object({
    /** The account, which every member of this composition already knows. */
    a: z.string().min(1).max(128),
    /** Which quadrant, or null when this room has never been told. */
    q: openWorldCoordinateSchema.nullable(),
    /** Is somebody at the controls. */
    c: z.boolean(),
    /** Is the hull still flying. */
    l: z.boolean(),
    /**
     * Roughly which way they are, when they are in another quadrant.
     *
     * Null when they are in THIS one - a marker on a hull you can already see
     * needs no compass - and null when the room does not know where they are.
     */
    d: openWorldDirectionSchema.nullable(),
  })
  .strict();

export type OpenWorldAllyState = z.infer<typeof openWorldAllyStateSchema>;

/**
 * The first frame a connection gets: who you are and where you are standing.
 *
 * It carries the quadrant's coordinate and its side, because a client cannot
 * clamp its own prediction to walls it does not know about. It carries nothing
 * else about the world: no danger rating, no resource rating, no space, no
 * count and no seed. A quadrant is discovered by being in it.
 */
export const openWorldWelcomeSchema = z
  .object({
    t: z.literal('w'),
    /** The protocol version this room speaks. A client that cannot, disconnects. */
    v: z.literal(OPEN_WORLD_ROOM_PROTOCOL_VERSION),
    /** Which quadrant. Indices, never the `F/10` caption - that is presentation. */
    q: openWorldCoordinateSchema,
    /** The quadrant's side in milli-units. Square, and solid on all four sides. */
    side: z.number().int().min(1).max(OPEN_WORLD_MAX_LOCAL_MU),
    /** Milliseconds of one authoritative tick. The client predicts at this rate. */
    tick: z.number().int().min(1),
    /** Your own entity id, for as long as this room holds your chair. */
    me: openWorldEntityIdSchema,
    /**
     * **The frozen composition you belong to** (version 4).
     *
     * Said once, on arrival, because it cannot change while the expedition is
     * under way - there is no invite, no kick and no swap. A client needs it to
     * know which accounts the ally view is about; sending it every frame would
     * be paying for a list that never moves.
     *
     * A solo pilot gets an expedition of one, which is not a special case: it
     * is what makes every rule downstream total.
     */
    ex: openWorldExpeditionSchema,
    /** The digest of that composition. Two hulls are allies when both match. */
    exd: openWorldExpeditionDigestSchema,
  })
  .strict();

export type OpenWorldWelcome = z.infer<typeof openWorldWelcomeSchema>;

/**
 * One authoritative frame, filtered to THIS connection's interest area.
 *
 * ## What a viewer is told, and what it is never told
 *
 * Its own hull, always. The hulls and shots inside its interest area, entering
 * and leaving explicitly. Nothing else: not how many players the quadrant
 * holds, not where the far ones are, not what they are fighting, not what the
 * quadrant contains and not a byte derived from the world's seed. The filtering
 * happens here, on the server, before the frame is built - never on the client,
 * which would mean sending it the answer and asking it not to look.
 */
export const openWorldStateSchema = z
  .object({
    t: z.literal('s'),
    /** The authoritative tick this photograph was taken on. */
    k: z.number().int().min(0),
    /**
     * The highest command sequence this connection has had applied.
     *
     * What a client reconciles its prediction against: everything at or below
     * this is already in the position below, and everything above it is still
     * only the client's guess.
     *
     * `-1` means this connection has had no command applied yet, and it is said
     * rather than collapsed onto `0`: zero is a legal first sequence, and a
     * client cannot tell "your first command landed" from "nothing has landed"
     * if the two are written the same way.
     */
    a: z.number().int().min(-1),
    /**
     * **This frame is the WHOLE view; forget anything not in it.**
     *
     * Set on the first frame after a room is rebuilt, because a rebuilt room
     * does not remember what any connection had been shown - the interest area
     * is presentation, and presentation is not in the snapshot. Without it a
     * client would keep drawing hulls that had drifted out of range while the
     * room was not running.
     *
     * Absent means the ordinary case: an incremental frame.
     */
    r: z.literal(1).optional(),
    /** Your own hull. Always present, whatever the interest area says. */
    me: openWorldEntityEntrySchema,
    /** Hulls that just entered the view. Full descriptors. */
    e: z.array(openWorldEntityEntrySchema),
    /** Hulls already in the view. State only. */
    u: z.array(openWorldEntityStateSchema),
    /** Hulls that left the view. Ids only - a viewer knows what to drop. */
    x: z.array(openWorldEntityIdSchema),
    /** Shots that just entered the view. */
    pe: z.array(openWorldProjectileEntrySchema),
    /**
     * **Nascimentos em lote** (versão 23). Ausente quer dizer "nenhum".
     *
     * Cada lote expande-se em entradas `pe` idênticas às que teriam viajado.
     * Um cliente processa `pb` **antes** de `pe`, e a ordem entre os dois não
     * importa: são corpos diferentes, e um lote nunca repete uma identidade que
     * já esteja em `pe`.
     */
    pb: z.array(openWorldProjectileBatchSchema).optional(),
    /** As salvas a entrar na vista (versão 32): ver `openWorldProjectileVolleySchema`. */
    pv: z.array(openWorldProjectileVolleySchema).optional(),
    /**
     * **Atualizações individuais.** Ausente quer dizer "nenhuma" (versão 24).
     *
     * A saída de escape que `pe` é para `pb`: hoje **nada** viaja por aqui,
     * porque toda a atualização que a sala produz tem os cinco valores e cabe
     * em `pm`. Ela fica para uma forma futura que os não tenha — uma correção
     * só de posição, por exemplo — em vez de obrigar essa forma a inventar
     * valores para caber num tuplo.
     */
    pu: z.array(openWorldProjectileStateSchema).optional(),
    /**
     * **Atualizações em LOTE** (versão 24). Ausente quer dizer "nenhuma".
     *
     * Um tuplo `[i, x, y, vx, vy]` por corpo, sem uma única chave repetida.
     * Identidades repetidas são recusadas: duas atualizações do mesmo corpo na
     * mesma trama seriam duas posições autoritativas para o mesmo instante.
     */
    pm: z
      .array(openWorldProjectileMoveSchema)
      .max(OPEN_WORLD_PROJECTILE_MOVE_MAX)
      .refine(
        (moves) => new Set(moves.map(([id]) => id)).size === moves.length,
        'a projectile move batch may not repeat an id',
      )
      .optional(),
    /** Shots that left the view, expired or spent. */
    px: z.array(z.number().int().min(1)),
    /** Hulls destroyed in view since the last frame. */
    d: z.array(openWorldDeathSchema),
    /** What the jewels did in view since the last frame. */
    j: z.array(openWorldJewelEventSchema),
    /**
     * **The allies**, and only for a connection that has some (version 4).
     *
     * Absent for a solo pilot, which is most frames of most sessions: an
     * expedition of one has nobody to report. Present for a member, listing
     * every OTHER member of the same frozen composition - including those in
     * other quadrants, which is the whole point of it.
     *
     * It is a private view: it is built per connection, from that connection's
     * own composition, and never merged into anything a stranger receives.
     */
    al: z.array(openWorldAllyStateSchema).optional(),
    /**
     * **Os cometas que entraram na vista** (versão 9). Descritores completos.
     *
     * Só os que estão na área de interesse deste jogador. Um quadrante tem
     * entre três e nove, e quantos ele tem ao todo não é uma coisa que uma
     * trama diga: publicar a contagem global seria publicar riqueza sem
     * ninguém ter ido lá ver.
     */
    ce: z.array(openWorldCometEntrySchema).optional(),
    /** Cometas já na vista que MUDARAM. Um cometa parado não aparece. */
    cu: z.array(openWorldCometStateSchema).optional(),
    /** Cometas que saíram da vista. Ids, e o cliente sabe o que largar. */
    cx: z.array(openWorldCometGoneSchema).optional(),
    /** O que lhes aconteceu em vista: impacto, fragmento, revelação, aquisição. */
    cv: z.array(openWorldCometEventSchema).optional(),
    /**
     * **Os planetas que entraram na vista** (versao 10). Descritores completos.
     *
     * Note o que NAO viaja: quantos nucleos o planeta tem. Um com um item e um
     * com tres entram exatamente da mesma maneira, e a diferenca so aparece
     * quando um nucleo fica exposto.
     */
    pe2: z.array(openWorldPlanetEntrySchema).optional(),
    /** Planetas ja na vista que MUDARAM. Um planeta parado nao aparece. */
    pu2: z.array(openWorldPlanetStateSchema).optional(),
    /** Planetas que sairam da vista. */
    px2: z.array(openWorldPlanetGoneSchema).optional(),
    /** O que lhes aconteceu: impacto, quebra, ouro, revelacao, item, reconstrucao. */
    pv2: z.array(openWorldPlanetEventSchema).optional(),
    /**
     * **Os mobs que entraram na vista** (versao 11). Descritores completos.
     *
     * So os que estao na area de interesse deste jogador. Quantos o quadrante
     * tem ao todo nao e um campo: publicar a contagem seria dizer a quem entra
     * quanto ha para enfrentar sem ele ter ido ver.
     */
    me2: z.array(openWorldMobEntrySchema).optional(),
    /** Mobs ja na vista que MUDARAM. Um mob parado nao aparece. */
    mu: z.array(openWorldMobStateSchema).optional(),
    /** Mobs que sairam da vista, ou que morreram. */
    mx: z.array(openWorldMobGoneSchema).optional(),
    /** O que lhes aconteceu: golpe, ataque, morte, nascimento. */
    mv: z.array(openWorldMobEventSchema).optional(),
    /**
     * **Os bosses que entraram na vista** (versao 12). Descritores completos.
     *
     * So os que estao na area de interesse deste jogador. **Nada sobre o pacote
     * deles**: nem o conteudo, nem a quantidade, nem sequer um campo onde isso
     * pudesse caber. O segredo e mantido pela AUSENCIA da forma, e nao por
     * alguem se lembrar de nao a preencher.
     */
    be: z.array(openWorldBossEntrySchema).optional(),
    /** Bosses ja na vista que MUDARAM, com a habilidade em curso. */
    bu: z.array(openWorldBossStateSchema).optional(),
    /** Bosses que sairam da vista, ou que deixaram de existir. */
    bx: z.array(openWorldBossGoneSchema).optional(),
    /** O que lhes aconteceu: golpe, contacto, lancamento, impacto, morte. */
    bv: z.array(openWorldBossEventSchema).optional(),
    /**
     * **Os teleguiados dos finais na vista** (versao 29).
     *
     * O estado INTEIRO de cada um, em cada pulsacao em que ele esta na vista.
     * Nao ha trama de entrada e trama de atualizacao separadas porque nao
     * compensaria: um teleguiado recompoe o rumo todos os tiques, portanto
     * **todos** os campos dele mudam sempre, e sao no maximo um por alvo e por
     * boss. **Nao viaja o dano, e nao viaja o alvo.**
     */
    bh: z.array(openWorldBossHomingSchema).optional(),
    /** Os teleguiados que acertaram, que se apagaram, ou que sairam da vista. */
    bhx: z.array(z.number().int().min(1)).optional(),
    /**
     * **Os nucleos runicos da Sentenca na vista** (versao 29).
     *
     * O estado inteiro, pela mesma razao: a vida deles muda enquanto alguem os
     * parte, e sao no maximo dois por boss. O limiar da interrupcao **nao viaja**
     * — ele e do servidor, e publica-lo daria um numero com que prever a
     * autoridade em vez de jogar.
     */
    bk: z.array(openWorldBossCoreSchema).optional(),
    /** Os nucleos partidos, expirados, ou que sairam da vista. */
    bkx: z.array(z.number().int().min(1)).optional(),
    /**
     * **A recusa por falta de espaco**, dita SO ao candidato do golpe fatal.
     *
     * O unico sitio em que a quantidade de itens de um boss e revelada, e ela e
     * revelada a quem acabou de nao caber com eles. Um espetador nunca a ve.
     */
    bc: z.array(openWorldBossCapacityRefusalSchema).optional(),
    /**
     * **As brasas na vista** (versao 26). Deixadas por um Rugido de Brasa.
     *
     * Entram, ardem e apagam-se. Nao se mexem — um lugar nao se mexe — portanto
     * nao ha uma trama de atualizacao para elas: o cliente conta o tempo pelo
     * milesimo que a entrada trouxe e pelo relogio autoritativo da trama.
     */
    bm: z.array(openWorldBossEmberSchema).optional(),
    /** As brasas que se apagaram, ou que sairam da vista. */
    bmx: z.array(z.number().int().min(1)).optional(),
    /**
     * **As invocacoes na vista** (versao 16): o Drone e o Leviata.
     *
     * Uma entidade propria, e nao um casco: ela tem posicao, vida e um corpo, e
     * a cena desenha-a de outra maneira. **Nao viaja** o tempo que lhe falta —
     * isso e informacao de decisao de quem a invocou.
     */
    sm: z.array(openWorldSummonEntrySchema).optional(),
    /**
     * **As explosoes de CARTA deste tique** (versao 17).
     *
     * Desenhadas uma vez e esquecidas: sao efeitos, e nao estado. Uma
     * ressincronizacao nao as traz — repetir uma explosao que ja aconteceu
     * seria inventar uma segunda.
     */
    kd: z.array(openWorldCardBurstSchema).optional(),
    /** Invocacoes que sairam da vista, ou que deixaram de existir. */
    sr: z.array(z.number().int().min(1)).optional(),
    /**
     * **As aguas na vista** (versao 13). Posicao, raio e semente decorativa.
     *
     * Nada sobre a populacao: saber quantos peixes uma agua tem sem la ir seria
     * saber onde vale a pena pescar sem pescar. O campo para isso nao existe.
     */
    wa: z.array(openWorldWaterAreaSchema).optional(),
    /** Aguas que sairam da vista. */
    wx: z.array(z.string().min(1).max(64)).optional(),
    /**
     * **Quem esta a pescar**, na vista. Entidade, agua e fase.
     *
     * Um espetador fica a saber que aquela nave esta ocupada e onde a linha
     * caiu. Nem a especie, nem o peso, nem quanto falta.
     */
    fp: z.array(openWorldFishingPublicSchema).optional(),
    /**
     * **A MINHA pescaria**, e so a minha.
     *
     * Enviada apenas a conexao do pescador, pelo mesmo mecanismo por conexao
     * que a recusa de capacidade de um boss usa. O peixe e `null` enquanto a
     * fase for `waiting` — nao e um campo escondido, e um campo que ainda nao
     * tem valor.
     */
    fm: openWorldFishingPrivateSchema.nullable().optional(),
    /**
     * **A agua em que o MEU casco esta**, ou nada.
     *
     * Derivada pelo servidor da posicao autoritativa e do raio do casco, e dita
     * so a esta conexao. E ela que decide se o botao existe: o cliente nao
     * conclui pela posicao que desenha, e nao nomeia uma area quando pede.
     */
    fw: z.string().min(1).max(64).nullable().optional(),
    /**
     * **O comerciante deste porto** (versao 14), ou nada.
     *
     * Publico: uma estacao comercial e um marco fixo, e quem a ve pode decidir
     * ir la. So existe num porto seguro — um quadrante perigoso nao tem campo
     * nenhum onde ela coubesse.
     */
    mk: openWorldMerchantPresenceSchema.nullable().optional(),
    /**
     * **Estou perto o suficiente para negociar**, decidido pelo servidor.
     *
     * Por conexao, e sobre o proprio casco. E ele que decide se o botao existe:
     * o cliente nao conclui pela posicao que desenha nem envia distancia.
     */
    mn: z.boolean().optional(),
    /**
     * **As caixas de morte na vista** (versao 15).
     *
     * Identidade, posicao e o tempo que resta — o suficiente para as desenhar e
     * para as escolher. **O conteudo e o Ouro nao viajam aqui**, e nao ha campo
     * onde coubessem: quem quiser saber o que uma caixa tem abre-a, e o que ela
     * responde (`lb`) vai so a quem a abriu.
     */
    ke: z.array(openWorldBoxMarkSchema).optional(),
    /** Caixas que sairam da vista, expiraram ou foram esvaziadas. */
    kx: z.array(openWorldBoxIdSchema).optional(),
    /**
     * **A caixa mais proxima que EU posso abrir**, ou nada.
     *
     * Por conexao e autoritativa: e ela que decide se o botao existe. O cliente
     * pode dizer qual caixa quer abrir, mas nao conclui proximidade da posicao
     * que desenha — e a autoridade revalida o id que receber.
     */
    kn: openWorldBoxIdSchema.nullable().optional(),
  })
  .strict();

export type OpenWorldStateFrame = z.infer<typeof openWorldStateSchema>;

/** Why a room refused something. A code, never a stack trace and never a value. */
export const OPEN_WORLD_ERROR_CODES = [
  'protocol',
  'rate_limit',
  'not_seated',
  'unavailable',
] as const;
export type OpenWorldErrorCode = (typeof OPEN_WORLD_ERROR_CODES)[number];

export const openWorldErrorSchema = z
  .object({ t: z.literal('e'), c: z.enum(OPEN_WORLD_ERROR_CODES) })
  .strict();

/**
 * Why a socket is being closed, said in the frame before the close.
 *
 * A game fact, not a transport one - which is why it travels here and the close
 * code stays 1000. `superseded` is the one a player sees when they open the
 * quadrant in a second tab: one account, one chair, and the newest connection
 * is the one that keeps it.
 */
export const OPEN_WORLD_CLOSE_REASONS = [
  'superseded',
  'protocol',
  'rate_limit',
  'unavailable',
  'destroyed',
  /**
   * **This hull is not in this quadrant any more** (version 3).
   *
   * Said by the ORIGIN once a crossing has committed. The socket is not
   * superseded - nobody took the chair - and the hull was not destroyed: it is
   * somewhere else, and this connection has no authority over it. The client
   * opens the destination with the token it was already given.
   */
  'departed',
] as const;
export type OpenWorldCloseReason = (typeof OPEN_WORLD_CLOSE_REASONS)[number];

export const openWorldClosingSchema = z
  .object({ t: z.literal('c'), c: z.enum(OPEN_WORLD_CLOSE_REASONS) })
  .strict();

export const openWorldServerFrameSchema = z.discriminatedUnion('t', [
  openWorldWelcomeSchema,
  openWorldStateSchema,
  openWorldErrorSchema,
  openWorldClosingSchema,
  /** Where a crossing this connection asked for got to (version 3). */
  openWorldCrossingResultSchema,
  /** What is inside a box, told ONLY to the connections looting it (version 5). */
  openWorldBoxViewSchema,
  /** What one loot order produced, told to the connection that asked (version 5). */
  openWorldLootResultSchema,
  /** A loot interaction ended, and why (version 5). */
  openWorldLootEndedSchema,
  /** This pilot's own cargo, which is the only thing they may do with it (version 5). */
  openWorldCargoViewSchema,
  /** Where this chair stands on the border, told only to it (version 6). */
  openWorldBorderStateSchema,
  /** How a channel ended, and whether an extraction is ready (version 6). */
  openWorldChannelResultSchema,
  /** A border order refused without anything happening to the hull (version 6). */
  openWorldBorderRefusalSchema,
  /**
   * **How the expedition ended** (version 7).
   *
   * The one frame that says a pilot has left the world for good - extracted or
   * dead - and where they go next. It is re-sent on a reconnection, because
   * somebody whose socket died half-way through the navigation still has to be
   * able to finish it, and reading a receipt repeats no transaction.
   */
  openWorldExtractionReceiptSchema,
  /**
   * **Como uma pescaria acabou** (versao 13), dito so ao pescador.
   *
   * Uma resposta por tentativa, com o id da ordem que a comecou: repetir a
   * ordem repete a resposta em vez de comecar uma segunda pescaria.
   */
  openWorldFishingResultSchema,
  /** Uma ordem de pescar recusada, ao pescador e com o id que ele enviou. */
  openWorldFishingRefusalSchema,
  /** **O balcao** (versao 14), dito so a quem esta diante dele. */
  openWorldMerchantViewSchema,
  /** **O recibo de uma venda**, com o preco realmente liquidado. */
  openWorldSaleReceiptSchema,
  /** Uma ordem de venda recusada, ao vendedor e com o id que ele enviou. */
  openWorldSaleRefusalSchema,
  /** **O conteudo da propria carga** (versao 15), dito so a uma conexao. */
  openWorldCargoContentsSchema,
  /** **Os tres espacos de cartas** (versao 16), ditos so a quem os tem. */
  openWorldCardHudSchema,
]);

export type OpenWorldServerFrame = z.infer<typeof openWorldServerFrameSchema>;
